package com.ijoin.ui.usercard

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.ijoin.data.model.UserProfile
import com.ijoin.data.repository.EventRepository
import com.ijoin.data.repository.UserRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class UserCardViewModel(
    private val userId: String,
    private val repository: UserRepository = UserRepository(),
    private val eventRepository: EventRepository = EventRepository()
) : ViewModel() {

    private val _uiState = MutableStateFlow(UserCardUiState())
    val uiState: StateFlow<UserCardUiState> = _uiState.asStateFlow()

    init {
        refresh()
    }

    fun refresh() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            repository.getUserProfile(userId)
                .onSuccess { profile ->
                    _uiState.update {
                        it.copy(
                            profile = profile,
                            isLoading = false,
                            error = null
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message
                        )
                    }
                }
        }
    }

    fun toggleFollow() {
        val profile = _uiState.value.profile ?: return
        if (profile.self) return

        val wasFollowing = profile.following
        viewModelScope.launch {
            _uiState.update { it.copy(isFollowUpdating = true, error = null) }
            val result = if (profile.following) {
                repository.unfollow(userId)
            } else {
                repository.follow(userId)
            }

            result
                .onSuccess { state ->
                    _uiState.update {
                        val updatedProfile = it.profile?.copy(
                            followersCount = state.followersCount,
                            followingCount = state.followingCount,
                            following = state.following
                        ) ?: profile.copy(
                            followersCount = state.followersCount,
                            followingCount = state.followingCount,
                            following = state.following
                        )
                        val shortcutMessage = if (!wasFollowing && state.following) {
                            updatedProfile?.let(UserCardMessage::ShortcutOffer)
                        } else null
                        it.copy(
                            profile = updatedProfile,
                            isFollowUpdating = false,
                            message = shortcutMessage ?: it.message
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            error = error.message,
                            isFollowUpdating = false
                        )
                    }
                }
        }
    }

    fun deleteEvent(eventId: String) {
        val profile = _uiState.value.profile ?: return
        if (profile.self.not()) return

        viewModelScope.launch {
            _uiState.update { it.copy(deletingEventId = eventId, error = null) }
            eventRepository.deleteEvent(eventId)
                .onSuccess {
                    _uiState.update { state ->
                        val currentProfile = state.profile
                        val updatedProfile = currentProfile?.copy(
                            upcomingEvents = currentProfile.upcomingEvents.filterNot { it.id == eventId },
                            archivedEvents = currentProfile.archivedEvents.filterNot { it.id == eventId }
                        )
                        state.copy(
                            profile = updatedProfile,
                            deletingEventId = null,
                            message = UserCardMessage.EventDeleted
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            deletingEventId = null,
                            error = error.message
                        )
                    }
                }
        }
    }

    fun clearError() {
        _uiState.update { it.copy(error = null) }
    }

    fun clearMessage() {
        _uiState.update { it.copy(message = null) }
    }
}

data class UserCardUiState(
    val isLoading: Boolean = true,
    val profile: UserProfile? = null,
    val error: String? = null,
    val isFollowUpdating: Boolean = false,
    val deletingEventId: String? = null,
    val message: UserCardMessage? = null
)

sealed interface UserCardMessage {
    data object EventDeleted : UserCardMessage
    data class ShortcutOffer(val profile: UserProfile) : UserCardMessage
}
